import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, TimeoutError, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';

/**
 * Таймаут одной попытки GET/HEAD-запроса (мс).
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Максимальное число повторов после исходной попытки (итого до 3 запросов).
 */
const MAX_RETRIES = 2;

/**
 * Токен контекста запроса: отключает повторы (retry) в RetryTimeoutInterceptor
 * для конкретного запроса. Таймаут одной попытки (REQUEST_TIMEOUT_MS)
 * при этом продолжает действовать.
 *
 * Использовать точечно, только для тяжёлых GET-эндпоинтов, где повторы
 * бесполезны или вредны (например, долгая статистика платежей: повторы
 * порождали волны дублирующих запросов на проде).
 */
export const SKIP_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Проверить, является ли ошибка восстанавливаемой (транзиентной).
 *
 * Ретраим только: сетевую ошибку браузера (status 0), собственный таймаут
 * (TimeoutError), серверные 5xx и rate-limit 429.
 * Любой другой 4xx (400/401/403/404/409/422...) НЕ ретраится — повторы
 * не должны усиливать брутфорс/спам и не имеют смысла при ошибках клиента.
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof TimeoutError) {
    return true;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  if (error.status === 0) {
    return true;
  }
  return error.status === 429 || (error.status >= 500 && error.status < 600);
}

/**
 * Интерцептор устойчивости к транзиентным сетевым сбоям.
 *
 * Применяется ТОЛЬКО к идемпотентным методам GET и HEAD:
 * - таймаут одной попытки 10 секунд;
 * - до 2 повторов с backoff 1 с и 2 с (итого до 3 попыток);
 * - ретраятся только восстанавливаемые ошибки (см. isRetryableError).
 *
 * Мутации (POST/PUT/PATCH/DELETE и прочие) проходят без изменений:
 * ретрай неидемпотентного запроса — риск двойного списания/дубля операции.
 *
 * Запрос не мутируется: заголовки (в т.ч. Authorization — зона HeaderInterceptor),
 * тело и параметры остаются как есть. После исчерпания повторов ошибка
 * пробрасывается дальше без изменений формата.
 */
@Injectable({
  providedIn: 'root',
})
export class RetryTimeoutInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method !== 'GET' && req.method !== 'HEAD') {
      return next.handle(req);
    }

    // Запросы с токеном SKIP_RETRY получают только таймаут попытки,
    // без повторов — см. описание токена.
    if (req.context.get(SKIP_RETRY)) {
      return next.handle(req).pipe(timeout(REQUEST_TIMEOUT_MS));
    }

    return next.handle(req).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({
        count: MAX_RETRIES,
        delay: (error, retryCount) => {
          // Не ретраим невосстанавливаемые ошибки — пробрасываем как есть.
          if (!isRetryableError(error)) {
            throw error;
          }
          // Backoff: 1 с перед первым повтором, 2 с перед вторым.
          // Логируем только method/url/status — без заголовков и тела.
          const status = error instanceof HttpErrorResponse ? error.status : 'timeout';
          console.warn(
            `RetryTimeoutInterceptor: повтор ${retryCount}/${MAX_RETRIES} ${req.method} ${req.url} (status: ${status})`,
          );
          return timer(retryCount * 1000);
        },
      }),
    );
  }
}
